/**
 * marketAnchor.js — "what does the top of the market go for in YOUR room?",
 * as a pure function.
 *
 * The user answers two dollar questions — what the 5th most expensive player
 * usually goes for, and what the most expensive usually goes for — and this
 * module returns the five soft-cap numbers that make the room CLEAR at those
 * prices, plus the ladder it predicts so the UI can echo the engine's own
 * arithmetic back.
 *
 * STICKERS DO NOT CHANGE. k and excessPass live on the BID side; the board is
 * untouched. Real rooms clear away from sticker and that gap IS the thing being
 * calibrated (fit design doc, "Design consequences" §1). Any copy built on this
 * module must say "will sell for around", never "is worth".
 *
 * No React, no hooks. Pure so the headless probes and the simConfig mirror
 * import the same code the browser runs — a hand-copied fit surface in the
 * mirror is exactly the drift the severance gate exists to catch.
 *
 * ── THE MAPPING IS MEASURED, NOT DERIVED ────────────────────────────
 * There is no formula from dollars to (k, excessPass). What exists is ~15k
 * seeded sims of the (k, excessPass) grid in two formats, recorded in
 * `docs/data/anchor-sweep-summary-aug12.md`. This file carries that grid as
 * DATA — the measured dollar ladders exactly as the summary prints them — and
 * inverts it numerically. The summary's tables are the source of truth; the
 * transcription below cites its section per row so the two can be diffed by eye.
 *
 * k is the LEVEL knob (summary §4/§10): it scales the whole anchor, and is
 * emulated exactly by floorFrac 0.30k / boardTerm 0.375k / headroom k−1,
 * because max(k·a, k·b) = k·max(a, b). The k = 1.00 cell reproduced the
 * directly-parameterised cell on the FNV digest and all 17 metrics in both
 * formats — the emulation is exact, not approximate.
 *
 * excessPass is the SHAPE knob (summary §11): dropping it compresses the ladder
 * rather than translating it — r1 falls ~3x as fast as r5 and r12 does not fall
 * at all.
 *
 * absCapFrac parks at 0.42 as a pure safety rail (pin ≤ 1.6% everywhere
 * measured). It is NOT a fitted parameter here. Two dead ends are recorded in
 * summary §6 and must not be relitigated: a fixed absCapFrac gap tied above
 * floorFrac (77.9% pin wall), and floorFrac alone (inert below 0.24, saturates
 * above 0.34).
 *
 * ── BOARD-RELATIVE IS WHAT MAKES IT FORMAT-FREE ─────────────────────
 * Everything is a ratio to the caller's OWN board price at the same rank. The
 * standard-vs-sflex ratio gap is 0.015–0.041 across the whole k range and
 * ≤ 0.025 for k ≤ 0.90 — i.e. tightest exactly in the region real rooms occupy
 * (summary §5, §10). So the two formats are POOLED into one curve here and no
 * per-format code exists anywhere in this feature. A 14-team non-PPR league
 * gets a coherent fit for free because its own board goes into the ratio.
 *
 * ── 🔴 LEVEL COMES FROM THE BOARD; SPREAD COMES FROM THE SURFACE ─────
 * That board-relative claim holds PER RANK, and only per rank. It does NOT
 * survive being used twice to reconstruct the gap BETWEEN two ranks, because
 * the two ratios have different denominators: r1 × boardR1 against
 * r5 × boardR5. The measured r1 ratio is systematically the lower of the two
 * (0.812 vs 0.934 at the hull floor — the most expensive player clears further
 * below his sticker than the 5th does), so the arithmetic only stays ordered
 * while the caller's board has a STEEP top. Both swept boards did: r1/r5 =
 * 1.265 (std) and 1.229 (sfx). Most shipping formats do not — 12- and 14-team
 * boards, and every SFLEX board, run 1.10–1.15 — and below about 1.15 the
 * reconstructed r1 dollars fall UNDER the r5 dollars and the echo tells the
 * user the 5th most expensive player will sell for more than the first
 * (measured across 8 shipping formats, Aug 13; 6 of 8 inverted).
 *
 * So the predicted ladder is built the way the measurement actually supports:
 *   • LEVEL from the board — r5$ = surface.r5 × boardR5, unchanged, the rank
 *     whose board-relative transfer is the better-behaved of the two.
 *   • SPREAD from the surface — r1$ = r5$ × `ladder`, the MEASURED r1$/r5$ of
 *     the sweep itself. It is not reconstructed from the caller's board gap at
 *     all, so a flat top of board can no longer invert it.
 * `ladder` is a genuinely stable measurement and that is why this is safe: it
 * reads 1.19–1.22 across BOTH formats and the ENTIRE k range, while tracking
 * excessPass hard (1.08 at x = 0.05 → 1.19 at x = 0.30). The spread the user
 * reads is therefore the shape knob's own signature, which is what it was
 * fitted to carry.
 *
 * 🔴 THIS IS THE ECHO ONLY. `derived` — the five values the engine reads — is
 * built from k and excessPass and is untouched by any of it, so no bid moves.
 * The engine's own cap is monotone in the player's board price by construction
 * (auction.js, "MONOTONE BY CONSTRUCTION"), so no draft ever sold rank 5 above
 * rank 1; the inversion lived only in this prediction.
 *
 * ── 🔴 THE 0.05 FLOOR ON excessPass IS A HARD FLOOR, NOT A SLIDER END ─
 * At excessPass 0.00 two pathologies fire, both MEASURED (summary §12):
 *   1. Prices quantise. Every cap collapses onto the player's own anchor, so
 *      the sale price IS that anchor and the top of every draft reads the same
 *      handful of exact dollar values — different names, identical prices.
 *   2. My-guy and sleeper boosts are deleted ENTIRELY. 0 of 37 boosted wins
 *      priced above the winner's own anchor, max $0.38 above; mean price/anchor
 *      0.954. The boost is worth literally nothing.
 * At 0.05 both break (top-2 spread $0.86–1.09, boosts worth ~$1) while the
 * ladder stays nearly as flat. Never emit below it.
 */

/**
 * The measured hull. `buildMarketAnchor` never emits outside it — every cell
 * inside was simulated, nothing outside was, and the levers are known to have
 * cliffs (excessPass at 0, fiftyPlus collapse at low k).
 */
// One definition of "which format is this", imported rather than re-derived.
// leagueFormat.js has no imports of its own, so this cannot cycle.
import { leagueFormatLabel } from './leagueFormat';

export const MARKET_ANCHOR_HULL = {
  k: { min: 0.65, max: 1.05 },
  excessPass: { min: 0.05, max: 0.30 },
};

/** The safety rail, flat whenever the anchor is active. Not fitted. */
export const MARKET_ANCHOR_ABS_CAP_FRAC = 0.42;

/** The stored shape. Both null = the OFF path. */
export const DEFAULT_MARKET_ANCHOR = { rank5Price: null, topPrice: null };


// ── PER-FORMAT ANCHOR DEFAULTS (aug28) — FRACTIONS OF BUDGET ────────
//
// 🔴 FRACTIONS, NOT DOLLARS, AND THAT IS THE WHOLE POINT. The backing measurement
// is $63.5 / $54, taken on a corpus normalised to $200. Storing those as flat
// dollars would tell a 12tm/$240 room its top is $63.5 when that room measurably
// clears $86.1 — the budget-unit bug class CLAUDE.md names as the project's most
// common (TIER_MAPS floor/ceiling are budget FRACTIONS; unscaled comparisons are
// the recurring defect). Expressed as a fraction it is one number that means the
// same thing in every room.
//
// THE BEFORE LEG THAT JUSTIFIES THE BASIS (n=200, anchor off, measured):
//     Stephen  12tm/$200   clears r1 $67.2  r5 $57.0
//     batchSim 12tm/$240   clears r1 $86.1  r5 $72.1
//     $86.1 x (200/240) = $71.75  ·  $72.1 x (200/240) = $60.08
// Normalised, they are the same room. That equivalence is what makes a fraction
// the correct basis and a flat dollar the wrong one.
//
// 🔴 EXTRAPOLATION CAVEAT, stated because it is real. The backing cell is
// `sflex 12tm $200`, n = 12 ROOMS. Any budget != $200 is an extrapolation resting
// on the linear-rescale assumption — the SAME assumption the corpus normalisation
// itself already makes, and one that is explicitly flagged as unproven elsewhere
// in this repo (SFLEX_BENCHMARKS: big-budget leagues were LINEARLY rescaled to
// $200 and measured perSlot is NOT actually scale-free across budgets). So this
// default is no more extrapolated than the number it came from — but it is not
// independent evidence for the assumption either.
//
// Room SD in that cell is ~$6.3. DO NOT TUNE THE THIRD DECIMAL — the second one
// is already inside the noise.
export const SFLEX_ANCHOR_TOP_FRAC = 0.3175;   // 63.5 / 200
export const SFLEX_ANCHOR_R5_FRAC = 0.27;      // 54   / 200

// ── THE 1QB PAIR — the measured Sleeper room (ships with chunk 5a) ──
//
// Same basis, same units, same caveat as the superflex pair above: FRACTIONS of
// budget, because $70 means one thing in a $200 room and another in a $240 one.
//
// THE MEASUREMENT, and why it is a DEFAULT rather than a preset the user opts
// into: the unanchored 1QB control does not clear where real Sleeper rooms do.
// Arm A (preset OFF) clears r1 $74 / r5 $64 against a Sleeper ladder of
// 70 / 65.5 / 62 / 60 / 57.5 — MAE $6.10 on the Aug 17 board, $6.00 on the
// Aug 26 one — and it does it sitting on the absCapFrac 0.37 rail (pin 11.6%)
// rather than on a price. Arm B, answering 70 / 57.5, clears
// 68 / 64 / 63 / 61 / 58.5 — MAE $1.30, all five rungs inside the corpus IQR,
// and the pin falls to 0. A default that is $4-7 closer to the real room on
// every rung is not a preference; leaving it off is the choice that needs the
// defence.
//
// Provenance: docs/data/platform-rooms-aug17.md (the Aug 18 rulings) and
// docs/data/platform-rooms-aug26.md (re-measured on the Aug 24-26 board:
// nothing moved beyond its own replicate bar, gate (a) MAE $1.30 <= $2.00,
// 5/5 rungs in IQR). Corroborated independently by the Aug 26 harvest ladder
// (r1 $68, hit exactly) and by docs/data/corpus-baselines-read-aug28.md, whose
// 1QB cell reads most-expensive $68.94 [66.3-73.0] and 5th $57.48 [54.2-60.7].
//
// The Yahoo pair (0.3675 / 0.305 = 73.5 / 61 at $200) is NOT here on purpose.
// It ships as a PRESET the user picks in MockSetup, not as a format default,
// because the Aug 18 ruling was level-only: Yahoo's real ladder is unusually
// flat between r1 and r2 ($73.5 -> $72.2) and a two-parameter (top, r5) anchor
// cannot bend to that shape. A default has to be right about shape as well as
// level; a preset the user chose only has to be their room.
//
// Room SD on the backing cell is dollars, not cents. DO NOT TUNE THE THIRD
// DECIMAL — the second one is already inside the noise.
export const ONEQB_ANCHOR_TOP_FRAC = 0.35;     // 70   / 200
export const ONEQB_ANCHOR_R5_FRAC = 0.2875;    // 57.5 / 200

// ── THE YAHOO PAIR — a PRESET, never a default ──────────────────────
//
// Measured the same way and on the same run as the Sleeper pair, and it is
// GOOD: arm C clears 72 / 69 / 67 / 65 / 62 against a real Yahoo ladder of
// 73.5 / 72.2 / 65.3 / 62.7 / 60.9, MAE $1.96.
//
// 🔴 IT IS NOT A DEFAULT AND THE REASON IS SHAPE, NOT LEVEL. Yahoo's real
// ladder is unusually flat across the top — $73.5 to $72.2 is a $1.30 drop from
// r1 to r2, where Sleeper's is $4.50 — and a two-parameter (top, r5) anchor has
// no knob that bends to it. The whole $1.96 is basically that one rung: r2
// misses by −3.2 while every other rung is inside ±2.3. That is the ruled,
// known limitation of this control, which is why the Aug 18 ruling was
// LEVEL-ONLY for Yahoo: ship it as something a user picks for their own room,
// not as something applied to rooms that never asked.
export const YAHOO_ANCHOR_TOP_FRAC = 0.3675;   // 73.5 / 200
export const YAHOO_ANCHOR_R5_FRAC = 0.305;     // 61   / 200

// The pre-ship pin, same pattern as `_qbTail` / `_flatCurve`. Shipping a format
// default moves every board in that format and therefore every digest recorded
// on it — superflex when the aug28 pair shipped, and SMITHTOWN/1QB TOO since
// chunk 5a, which is the first anchor default to reach the calibration
// baseline. So the recipes whose committed artifacts pre-date a ship need to be
// able to see the engine they were recorded against. `enabled: false` makes
// `defaultAnchorForFormat` return null for EVERY format, which is exactly the
// pre-default behaviour (no default → sanitize passes the stored nulls through
// → anchor OFF).
//
// 🔴 THREE THINGS DEPEND ON THIS ESCAPE AND ARE NOT OPTIONAL:
// `softcapProbe.mjs` (the pin, plus `gate()`'s own `withAnchorDefaults`),
// `src/tools/severanceGate.test.js` (its pinned contamination digests), and
// `src/tools/marketAnchorGate.test.js` (gate (1), "an unanswered anchor is the
// shipped engine byte for byte" — which on a 1QB room is only reachable here).
// Deleting or defaulting this to false would silently repoint all three.
export const _anchorDefaults = { enabled: true };

/** Round to the input control's half-dollar step, at application time. */
const halfDollar = (v) => Math.round(v * 2) / 2;

/**
 * Format label -> the pair, or ABSENT for "this format has no default".
 *
 * A table rather than a chain of `if (label !== 'sflex') return null`, because
 * the chain is what let the 1QB branch sit on a refuted comment for a ship
 * cycle: a missing format read as a deliberate null and as an unexamined
 * fall-through in exactly the same way. Here an absent key IS the statement,
 * and '2qb' is absent on the record above.
 */
const ANCHOR_DEFAULT_FRACS = {
  '1qb': { top: ONEQB_ANCHOR_TOP_FRAC, r5: ONEQB_ANCHOR_R5_FRAC },
  sflex: { top: SFLEX_ANCHOR_TOP_FRAC, r5: SFLEX_ANCHOR_R5_FRAC },
};

/**
 * The format's default anchor, in this room's dollars — or null for no default.
 *
 * 1QB: the Sleeper pair. 🔴 THE OLD COMMENT HERE SAID "current behaviour IS the
 * pooled/1QB measurement, so a default would be re-anchoring the room to
 * itself". THAT WAS REFUTED BY MEASUREMENT — Aug 17, re-confirmed Aug 26. The
 * unanchored 1QB control clears $4-7.50 ABOVE the Sleeper corpus on every rung
 * (MAE $6.10 against $1.00-1.30 anchored), and it does it by sitting on the
 * absCapFrac rail rather than on a price. It was not the room measuring itself;
 * it was the room measuring the rail. Provenance:
 * docs/data/platform-rooms-aug17.md and docs/data/platform-rooms-aug26.md.
 * 2QB: null, DELIBERATELY. The corpus has two 2QB rooms in one league; that
 * cannot answer what a 2QB board should clear at, and inventing a number here
 * would launder a guess into a default. Revisit when the corpus can answer.
 * SFLEX: its own fractions, unchanged by this ship.
 *
 * @param {object} positions - settings.positions
 * @param {number} budget    - the room's budget
 * @returns {{rank5Price:number, topPrice:number}|null}
 */
export function defaultAnchorForFormat(positions, budget) {
  if (!_anchorDefaults.enabled) return null;
  if (!isPos(budget)) return null;
  const fracs = ANCHOR_DEFAULT_FRACS[leagueFormatLabel(positions)];
  if (!fracs) return null;
  return {
    topPrice: halfDollar(fracs.top * budget),
    rank5Price: halfDollar(fracs.r5 * budget),
  };
}

/**
 * The anchor actually in force: the user's values where set, the format default
 * where not.
 *
 * USER VALUES ALWAYS WIN, and CLEARING A FIELD RETURNS TO THE FORMAT DEFAULT
 * rather than to raw OFF — a cleared box means "I have no opinion", not "turn the
 * model off", and the two used to be indistinguishable because OFF was the only
 * fallback. Per FIELD, not per object, so setting one box does not silently
 * discard the default for the other.
 */
export function resolveMarketAnchor(rawAnchor, positions, budget) {
  const s = sanitizeMarketAnchor(rawAnchor);
  const def = defaultAnchorForFormat(positions, budget);
  if (!def) return s;
  return {
    topPrice: s.topPrice === null ? def.topPrice : s.topPrice,
    rank5Price: s.rank5Price === null ? def.rank5Price : s.rank5Price,
  };
}

// ── PLATFORM PRESETS — "where's your real draft?" ───────────────────
//
// 🔴 THE PRESETS ARE A UI AFFORDANCE OVER THE EXISTING TWO BOXES, NOT A SECOND
// CHANNEL INTO THE ENGINE. Clicking one writes its pair into
// `settings.marketAnchor` as ordinary user values; from there everything
// downstream — `resolveMarketAnchor`, `buildMarketAnchor`, the rider, the echo
// — is byte-identical to a user who typed those two numbers. No preset name is
// stored, no preset name reaches `calcAiMaxBidV2`, and nothing here changes the
// resolver's semantics.
//
// WHICH IS WHY THE SELECTION IS DERIVED, NOT PERSISTED. `marketAnchorPresetFor`
// answers "which preset does the pair CURRENTLY IN FORCE correspond to?" by
// comparing against the presets' own budget-scaled pairs. A stored preset key
// would be a second source of truth for a question the numbers already answer,
// and it could disagree with them — a league saved as "yahoo" whose values were
// later edited would still report yahoo forever, in the UI and in analytics.
// Derived, the chip and the event cannot drift from the anchor actually in
// force, and no saved-league migration is needed.
//
// Sleeper is defined FROM the 1QB default constants rather than as fresh
// literals: they are the same measurement, and a copy of it here could be tuned
// on one side only.
export const MARKET_ANCHOR_PRESETS = {
  sleeper: {
    key: 'sleeper',
    label: 'Sleeper',
    top: ONEQB_ANCHOR_TOP_FRAC,
    r5: ONEQB_ANCHOR_R5_FRAC,
    // 🔴 NO LEAGUE COUNT IN THE COPY. The corpus is ~430 leagues today and
    // grows with every harvest; a number baked into a string is wrong by the
    // next one and nobody re-reads UI copy when a corpus changes.
    provenance: 'calibrated on real Sleeper auction leagues',
  },
  yahoo: {
    key: 'yahoo',
    label: 'Yahoo',
    top: YAHOO_ANCHOR_TOP_FRAC,
    r5: YAHOO_ANCHOR_R5_FRAC,
    provenance: 'Yahoo default salary-cap rooms, Aug 16 snapshot',
  },
};

/** The label for "these numbers are the user's own", not a preset. */
export const MARKET_ANCHOR_CUSTOM = 'custom';

/**
 * A preset's pair in THIS room's dollars — same fraction-of-budget basis and
 * same half-dollar step as `defaultAnchorForFormat`, so a preset and a default
 * that share a measurement produce the identical pair at every budget.
 *
 * @returns {{rank5Price:number, topPrice:number}|null}
 */
export function marketAnchorPresetPair(preset, budget) {
  const p = MARKET_ANCHOR_PRESETS[preset];
  if (!p || !isPos(budget)) return null;
  return { topPrice: halfDollar(p.top * budget), rank5Price: halfDollar(p.r5 * budget) };
}

/**
 * Every preset's pair in this room's dollars, keyed as MARKET_ANCHOR_PRESETS is
 * — or null when the budget cannot produce one. The UI takes the whole map
 * rather than calling `marketAnchorPresetPair` per chip so the chip row, the
 * re-fill rule and the provenance line are all reading one object.
 *
 * @returns {null | Record<string, {rank5Price:number, topPrice:number}>}
 */
export function marketAnchorPresetPairs(budget) {
  if (!isPos(budget)) return null;
  const out = {};
  for (const key of Object.keys(MARKET_ANCHOR_PRESETS)) out[key] = marketAnchorPresetPair(key, budget);
  return out;
}

// Both sides are half-dollar quantities, so this is an exact comparison with a
// float guard on it — not a tolerance that could make two presets collide.
const sameDollar = (a, b) => a !== null && b !== null && Math.abs(a - b) < 1e-6;

/**
 * Which preset the anchor IN FORCE corresponds to — 'sleeper' | 'yahoo' |
 * 'custom'. One definition, read by the chip row and by the
 * `mock_draft_started` property, so the screen and the analytics cannot
 * disagree about what the user is drafting against.
 *
 * Note it resolves FIRST. That is what makes a fresh 1QB room read 'sleeper'
 * with both boxes empty — the format default IS the Sleeper pair, so the pair
 * in force really is Sleeper's, and reporting 'custom' there would be false.
 * A fresh SFLEX room reads 'custom' because its default is the superflex pair,
 * which is neither preset; a 2QB room reads 'custom' because it has no anchor
 * at all.
 *
 * @param {object} rawAnchor - settings.marketAnchor, as stored
 * @param {object} positions - settings.positions
 * @param {number} budget    - the room's budget
 * @returns {'sleeper'|'yahoo'|'custom'}
 */
export function marketAnchorPresetFor(rawAnchor, positions, budget) {
  const inForce = resolveMarketAnchor(rawAnchor, positions, budget);
  if (inForce.topPrice === null || inForce.rank5Price === null) return MARKET_ANCHOR_CUSTOM;
  for (const key of Object.keys(MARKET_ANCHOR_PRESETS)) {
    const pair = marketAnchorPresetPair(key, budget);
    if (pair && sameDollar(pair.topPrice, inForce.topPrice)
      && sameDollar(pair.rank5Price, inForce.rank5Price)) return key;
  }
  return MARKET_ANCHOR_CUSTOM;
}

// ── THE MEASURED SURFACE ────────────────────────────────────────────
// Boards are the dynamicPrices sticker ladders the sweep ran against — static
// per format, read once, printed in summary §"The boards" and re-confirmed in
// both .json sidecars' `board_ladder`. They are the DENOMINATORS that turn the
// measured dollars into format-free ratios; they are NOT defaults for a
// caller's board, which always comes in as an argument.
const MEASURED_BOARD = {
  std: { r1: 62, r5: 49 },   // standard 10tm/$200
  sfx: { r1: 59, r5: 48 },   // Stephen SFLEX 12tm/$200
};

/**
 * The LEVEL curve: k → clearing ladder, at the shipped excessPass 0.30.
 * Dollars exactly as printed. `std`/`sfx` are [r1, r5].
 *
 * k 0.65–0.75 are summary §10 (shape chunk A); k 0.80–1.05 are summary §4
 * (chunk C, the k-sweep), whose k = 1.00 row is the control that reproduced
 * chunk 1's and chunk B's independently-run cells to the cent.
 */
const LEVEL_ROWS = [
  { k: 0.65, std: [59.65, 50.19], sfx: [57.94, 47.95] },   // §10
  { k: 0.70, std: [61.74, 51.89], sfx: [60.20, 49.95] },   // §10
  { k: 0.75, std: [64.32, 53.81], sfx: [62.19, 52.00] },   // §10
  { k: 0.80, std: [66.67, 55.92], sfx: [64.44, 53.81] },   // §4
  { k: 0.85, std: [68.90, 57.70], sfx: [66.41, 55.77] },   // §4
  { k: 0.90, std: [71.25, 59.53], sfx: [68.47, 57.48] },   // §4
  { k: 0.95, std: [73.55, 61.71], sfx: [70.58, 59.13] },   // §4
  { k: 1.00, std: [75.72, 63.19], sfx: [72.95, 59.94] },   // §4 (the control cell)
  { k: 1.05, std: [77.84, 64.30], sfx: [75.16, 61.04] },   // §4
];

/**
 * The SHAPE grid: excessPass × k, summary §11 (shape chunk B). Only k 0.80 and
 * 0.90 were swept on excessPass — that is the whole measured x-dependence.
 *
 * The x = 0.30 column is NOT repeated here; it comes from LEVEL_ROWS above, so
 * the shape factor at 0.30 is exactly 1.0 by construction and the surface
 * reproduces §4 to the cent along that edge.
 *
 * 🔴 x = 0.00 IS EMBEDDED BUT UNREACHABLE. The hull floor is 0.05. The row is
 * kept because the 0.05 knot is an interpolation endpoint that reads more
 * honestly next to its neighbour, and because deleting measured data to make a
 * clamp look tighter is how a hull stops being auditable. `fitOnSurface` never
 * proposes x < MARKET_ANCHOR_HULL.excessPass.min.
 */
const SHAPE_ROWS = [
  { k: 0.80, x: 0.00, std: [53.00, 49.80], sfx: [51.99, 48.23] },   // §11
  { k: 0.80, x: 0.05, std: [55.55, 50.93], sfx: [53.96, 50.32] },   // §11
  { k: 0.80, x: 0.10, std: [57.56, 52.19], sfx: [55.82, 51.48] },   // §11
  { k: 0.80, x: 0.20, std: [61.97, 53.99], sfx: [59.94, 52.58] },   // §11
  { k: 0.90, x: 0.00, std: [59.93, 55.37], sfx: [57.94, 53.74] },   // §11
  { k: 0.90, x: 0.05, std: [61.87, 56.44], sfx: [60.03, 55.23] },   // §11
  { k: 0.90, x: 0.10, std: [63.62, 57.08], sfx: [61.54, 55.76] },   // §11
  { k: 0.90, x: 0.20, std: [67.39, 58.64], sfx: [64.93, 56.70] },   // §11
];

/** The two formats' ratios, averaged. See the format-portability note above. */
function pooledRatios(row) {
  return {
    r1: (row.std[0] / MEASURED_BOARD.std.r1 + row.sfx[0] / MEASURED_BOARD.sfx.r1) / 2,
    r5: (row.std[1] / MEASURED_BOARD.std.r5 + row.sfx[1] / MEASURED_BOARD.sfx.r5) / 2,
  };
}

/**
 * The measured clearing LADDER — r1$ / r5$ within a row, pooled across the two
 * formats. No board appears in it at either end: it is the sweep's own top-two
 * gap, which is exactly why it survives being carried onto a board whose top is
 * shaped differently from the two that were swept.
 *
 * Pooled as the mean of the two formats' ladders rather than derived from
 * `pooledRatios` — mean-of-ratios and ratio-of-means differ here, and this is
 * the one that is a straight average of two measurements.
 */
function pooledLadder(row) {
  return (row.std[0] / row.std[1] + row.sfx[0] / row.sfx[1]) / 2;
}

const LEVEL_K = LEVEL_ROWS.map(r => r.k);
const LEVEL_R1 = LEVEL_ROWS.map(r => pooledRatios(r).r1);
const LEVEL_R5 = LEVEL_ROWS.map(r => pooledRatios(r).r5);
const LEVEL_LADDER = LEVEL_ROWS.map(pooledLadder);

// Shape knots in x, ascending. 0.30 is the LEVEL curve itself (factor 1.0).
const SHAPE_X = [0.00, 0.05, 0.10, 0.20, 0.30];
const SHAPE_ANCHOR_K = [0.80, 0.90];

/**
 * The shape FACTOR, not the ratio: measured(k, x) ÷ measured(k, 0.30). Storing
 * the ratio-of-ratios rather than the raw surface is what lets one x-sweep at
 * two k values extend across the whole k range — the level curve carries k, the
 * factor carries x.
 */
function buildShapeFactors(kAnchor) {
  const level = pooledRatios(LEVEL_ROWS.find(r => r.k === kAnchor));
  const r1 = [], r5 = [];
  for (const x of SHAPE_X) {
    if (x === 0.30) { r1.push(1); r5.push(1); continue; }
    const cell = pooledRatios(SHAPE_ROWS.find(r => r.k === kAnchor && r.x === x));
    r1.push(cell.r1 / level.r1);
    r5.push(cell.r5 / level.r5);
  }
  return { r1, r5 };
}

const SHAPE_F = SHAPE_ANCHOR_K.map(buildShapeFactors);

/**
 * The same factor construction for the ladder. This is where excessPass shows
 * up in the echo: the ladder compresses from 1.19 to 1.08 as x falls to the
 * hull floor, which IS the shape knob's measured signature (summary §11 — r1
 * falls ~3x as fast as r5).
 */
function buildLadderFactors(kAnchor) {
  const level = pooledLadder(LEVEL_ROWS.find(r => r.k === kAnchor));
  return SHAPE_X.map(x =>
    x === 0.30 ? 1 : pooledLadder(SHAPE_ROWS.find(r => r.k === kAnchor && r.x === x)) / level
  );
}

const SHAPE_LADDER_F = SHAPE_ANCHOR_K.map(buildLadderFactors);

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const isPos = (v) => isNum(v) && v > 0;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Piecewise-linear, clamped flat outside the knots. */
function interp1(xs, ys, x) {
  const n = xs.length;
  if (x <= xs[0]) return ys[0];
  if (x >= xs[n - 1]) return ys[n - 1];
  for (let i = 1; i < n; i++) {
    if (x <= xs[i]) {
      const t = (x - xs[i - 1]) / (xs[i] - xs[i - 1]);
      return ys[i - 1] + t * (ys[i] - ys[i - 1]);
    }
  }
  return ys[n - 1];
}

/**
 * The interpolated surface: (k, excessPass) → predicted clearing ladder as
 * ratios to the caller's board.
 *
 * 🔴 THE SHAPE FACTOR IS HELD FLAT IN k OUTSIDE [0.80, 0.90] — deliberately not
 * extrapolated. The factor does trend with k (excessPass matters slightly less
 * as k rises), and continuing that trend to k = 0.65 would predict a further
 * ~$3 of r1 compression that nothing measured. Holding flat under-claims at the
 * edges; extrapolating would invent a measurement. The level curve, which IS
 * measured across the full k range, carries the k-dependence that matters.
 */
export function marketAnchorSurface(k, x) {
  const t = clamp((k - SHAPE_ANCHOR_K[0]) / (SHAPE_ANCHOR_K[1] - SHAPE_ANCHOR_K[0]), 0, 1);
  const fR1 = interp1(SHAPE_X, SHAPE_F[0].r1, x) * (1 - t) + interp1(SHAPE_X, SHAPE_F[1].r1, x) * t;
  const fR5 = interp1(SHAPE_X, SHAPE_F[0].r5, x) * (1 - t) + interp1(SHAPE_X, SHAPE_F[1].r5, x) * t;
  const fL = interp1(SHAPE_X, SHAPE_LADDER_F[0], x) * (1 - t) + interp1(SHAPE_X, SHAPE_LADDER_F[1], x) * t;
  return {
    r1: interp1(LEVEL_K, LEVEL_R1, k) * fR1,
    r5: interp1(LEVEL_K, LEVEL_R5, k) * fR5,
    // Board-free. `r1` above is what the FIT is scored against (it is the half
    // of the user's answer that names a dollar figure for rank 1); `ladder` is
    // what the ECHO is built from. See the level/spread note in the header.
    ladder: interp1(LEVEL_K, LEVEL_LADDER, k) * fL,
  };
}

/**
 * The narrowest ladder the hull can emit — the "minimum spread" the echo is
 * guaranteed to show, as a multiplier on the rank-5 dollars. Scanned off the
 * surface at module load rather than transcribed, so it cannot drift from the
 * thing it describes the way a hand-copied constant would.
 *
 * ~1.081, at the excessPass floor. On a $50 rank-5 that is a $4 gap; on the
 * two swept boards it is $4.3–4.7, which is the ≥$3.50 spread the sweep
 * reported. It is a RATIO, not a dollar amount, so it shrinks with the board —
 * a $100-budget league gets proportionally less, correctly.
 */
export const MARKET_ANCHOR_MIN_LADDER = (() => {
  const K = MARKET_ANCHOR_HULL.k, X = MARKET_ANCHOR_HULL.excessPass;
  let min = Infinity;
  for (let i = 0; i <= 80; i++) {
    const k = K.min + ((K.max - K.min) * i) / 80;
    for (let j = 0; j <= 50; j++) {
      min = Math.min(min, marketAnchorSurface(k, X.min + ((X.max - X.min) * j) / 50).ladder);
    }
  }
  return min;
})();

/**
 * Least-squares nearest point on the surface, searched on a grid inside the
 * hull. Coarse (0.005) then refined (0.0005) around the winner — the surface is
 * piecewise-bilinear and monotone in both arguments, so a local refine cannot
 * miss a distant basin.
 *
 * r1 and r5 residuals are weighted EQUALLY and measured in ratio space, where
 * both live near 1.0. Weighting r5 higher is tempting (it is the more reliable
 * of the two user answers) but it would let a badly-fit r1 pass silently into
 * the UI echo, where the user reads it as a promise.
 */
function fitOnSurface(targetR1, targetR5) {
  const K = MARKET_ANCHOR_HULL.k, X = MARKET_ANCHOR_HULL.excessPass;
  let best = { k: K.min, x: X.min, err: Infinity };

  const scan = (kLo, kHi, kSteps, xLo, xHi, xSteps) => {
    for (let i = 0; i <= kSteps; i++) {
      const k = clamp(kLo + ((kHi - kLo) * i) / kSteps, K.min, K.max);
      for (let j = 0; j <= xSteps; j++) {
        const x = clamp(xLo + ((xHi - xLo) * j) / xSteps, X.min, X.max);
        const s = marketAnchorSurface(k, x);
        const err = (s.r1 - targetR1) ** 2 + (s.r5 - targetR5) ** 2;
        if (err < best.err) best = { k, x, err };
      }
    }
  };

  scan(K.min, K.max, 80, X.min, X.max, 50);           // 0.005 / 0.005
  scan(best.k - 0.005, best.k + 0.005, 20,
       best.x - 0.005, best.x + 0.005, 20);           // 0.0005 / 0.0005

  return { k: Math.round(best.k * 1e4) / 1e4, excessPass: Math.round(best.x * 1e4) / 1e4 };
}

/**
 * The whole feature, as one call.
 *
 * @param {{rank5Price:number, topPrice:number}} answers - the user's two dollar
 *   answers. Either missing, non-numeric or non-positive → OFF.
 * @param {number} boardR5 - the caller's own 5th-highest sticker price.
 * @param {number} boardR1 - the caller's own highest sticker price.
 * @returns {null | {k, excessPass, derived, predicted}} `null` is the OFF path
 *   and it means BIT-IDENTICAL: the caller must attach nothing and the engine
 *   must read the shipped constants byte-for-byte. Never returns a
 *   "neutral" object — the same returns-null-not-1 discipline as
 *   `buildUserPositionMultipliers`, for the same reason (an inert gate is only
 *   meaningful if OFF skips the code path entirely).
 *
 *   Garbage in returns null rather than NaN. A NaN in `derived` would reach
 *   `calcAiMaxBidV2` and poison every bid in the draft.
 */
export function buildMarketAnchor(answers, boardR5, boardR1) {
  if (!answers || typeof answers !== 'object') return null;
  if (!isPos(answers.rank5Price) || !isPos(answers.topPrice)) return null;
  if (!isPos(boardR5) || !isPos(boardR1)) return null;

  const targetR5 = answers.rank5Price / boardR5;
  const targetR1 = answers.topPrice / boardR1;
  if (!isNum(targetR5) || !isNum(targetR1)) return null;

  const { k, excessPass } = fitOnSurface(targetR1, targetR5);
  if (!isNum(k) || !isNum(excessPass)) return null;

  // Recomputed at the ROUNDED (k, x) that ships, so the number the UI echoes is
  // the number the engine used — the describePositionDials discipline.
  //
  // LEVEL from the board, SPREAD from the surface (header, "🔴 LEVEL COMES FROM
  // THE BOARD"). r1 is deliberately NOT s.r1 × boardR1: that reconstruction
  // divides by the caller's own top-of-board gap, which inverts on any board
  // flatter than the two that were swept. `boardR1` therefore no longer appears
  // downstream of the fit at all — it is an input to the FIT (it is how the
  // user's rank-1 dollar answer becomes a ratio) and nothing else.
  const s = marketAnchorSurface(k, excessPass);
  const r5 = s.r5 * boardR5;
  const r1 = r5 * s.ladder;
  if (!isNum(r1) || !isNum(r5)) return null;

  return {
    k,
    excessPass,
    // The five values that replace the `_softCap` fields. k emulation is exact:
    // floorFrac 0.30k / boardTerm 0.375k / headroom k−1 (summary §4).
    derived: {
      floorFrac: 0.30 * k,
      boardTerm: 0.375 * k,
      headroom: k - 1,
      excessPass,
      absCapFrac: MARKET_ANCHOR_ABS_CAP_FRAC,
    },
    predicted: {
      r1: Math.round(r1 * 100) / 100,
      r5: Math.round(r5 * 100) / 100,
    },
  };
}

/**
 * The one call both boards make: settings.marketAnchor + a dynamicPrices map →
 * the rider to attach, or null.
 *
 * The board ladder is read off the CALLER'S map, which is why this lives here
 * rather than being open-coded twice. usePricing and simConfig.
 * buildDynamicPriceMap build the same board by two separate code paths; if each
 * also picked its own r1/r5 off it, a one-line divergence (`.slice(0,5)` vs
 * rank indexing, a stray filter) would move the fit in the browser and not in
 * the probes — the same class of hole the dial mirror was.
 *
 * r5 is the 5th-highest sticker OVERALL (cross-position sale rank), matching
 * `boardLadder` in softcapProbe.mjs and the summary's board rows. A map with
 * fewer than five entries has no r5 and returns null (off), not a guess.
 *
 * @returns {null | object} the full `buildMarketAnchor` result — callers hang
 *   `.derived` on the map as `__marketAnchor` and the UI echoes `.predicted`
 *   from this same call, so the screen and the engine can never disagree.
 *   Null means attach NOTHING (the bit-identical OFF path).
 */
export function marketAnchorFromPriceMap(rawAnchor, priceMap) {
  if (marketAnchorIsOff(rawAnchor)) return null;
  if (!priceMap || typeof priceMap.values !== 'function') return null;
  const sorted = [...priceMap.values()].sort((a, b) => b - a);
  if (sorted.length < 5) return null;
  return buildMarketAnchor(sanitizeMarketAnchor(rawAnchor), sorted[4], sorted[0]);
}

/** Shape-check a stored anchor, for the settings/league round trip. */
export function sanitizeMarketAnchor(raw) {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_MARKET_ANCHOR };
  return {
    rank5Price: isPos(raw.rank5Price) ? raw.rank5Price : null,
    topPrice: isPos(raw.topPrice) ? raw.topPrice : null,
  };
}

/** True when the stored anchor cannot produce a fit — the cheap OFF check. */
export function marketAnchorIsOff(raw) {
  const s = sanitizeMarketAnchor(raw);
  return s.rank5Price === null || s.topPrice === null;
}
